import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";
import { DESIGN_VARIANTS, type DesignVariant } from "../../shared/constants";

interface Proposal {
  id: string;
  variant: DesignVariant;
  image_url: string | null;
}

interface SiteAnalysis {
  source_url: string;
  strengths: string | null;
  services: string[];
  tone: string | null;
  color_tendency: string | null;
  improvements: string | null;
}

const VARIANT_LABEL: Record<string, string> = Object.fromEntries(
  DESIGN_VARIANTS.map((v) => [v.value, v.label]),
);
const VARIANT_DESC: Record<string, string> = {
  standard: "信頼感を重視した定番の安心感あるデザイン",
  modern: "先進的で洗練されたテクノロジー感のあるデザイン",
  minimal: "余白を活かしたシンプルで美しいデザイン",
  natural: "やさしい色合いで親しみやすいナチュラルテイスト",
  premium: "高級感と重厚感を演出するプレミアムデザイン",
  creative: "個性と創造性を表現したクリエイティブなデザイン",
};

// No.7 トップページ案一覧・選択。
export function DesignSelectPage() {
  const { caseRow, loading } = useCurrentCase();
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [analysis, setAnalysis] = useState<SiteAnalysis | null>(null);

  useEffect(() => {
    if (!caseRow) return;
    api
      .get<{ proposals: Proposal[]; selected_proposal_id: string | null }>(
        `/api/cases/${caseRow.id}/proposals`,
      )
      .then((res) => {
        setProposals(res.proposals);
        setSelectedId(res.selected_proposal_id ?? res.proposals[0]?.id ?? null);
      })
      .finally(() => setFetching(false));
    // 既存サイト解析(あれば)サマリー表示。失敗時は従来どおり非表示。
    api
      .get<{ analysis: SiteAnalysis | null }>(`/api/cases/${caseRow.id}/analysis`)
      .then((res) => setAnalysis(res.analysis))
      .catch(() => setAnalysis(null));
  }, [caseRow]);

  const proceed = async () => {
    if (!caseRow || !selectedId) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/api/cases/${caseRow.id}/proposals/${selectedId}/select`, {});
      navigate("/design/tune");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "選択に失敗しました");
      setSubmitting(false);
    }
  };

  if (loading || fetching) return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">案件が見つかりません。</div>;
  if (proposals.length === 0) {
    return (
      <div className="page-center">
        デザイン案がまだありません。
        <button className="btn btn-ghost" onClick={() => navigate("/generating")}>
          生成状況を確認
        </button>
      </div>
    );
  }

  const selected = proposals.find((p) => p.id === selectedId) ?? proposals[0]!;

  return (
    <section className="wizard">
      <div className="wizard-head">
        <span className="step-tag">No.7</span>
        <h1>トップページ案一覧・選択</h1>
        <p className="muted">
          AIが複数のトップページデザイン案をご提案しました。お好みのデザインを選択して、制作を進めましょう。
        </p>
      </div>

      {analysis && (
        <div className="card analysis-card">
          <div className="analysis-head">
            <span className="analysis-badge">既存サイト解析</span>
            <h2>現在のサイトを分析し、6案に反映しました</h2>
            <p className="muted small">
              解析対象:{" "}
              <a href={analysis.source_url} target="_blank" rel="noreferrer">
                {analysis.source_url}
              </a>
            </p>
          </div>
          <div className="analysis-grid">
            {analysis.strengths && (
              <div className="analysis-item">
                <span className="analysis-label">強み（活かす）</span>
                <p>{analysis.strengths}</p>
              </div>
            )}
            {analysis.services.length > 0 && (
              <div className="analysis-item">
                <span className="analysis-label">主要サービス</span>
                <div className="analysis-chips">
                  {analysis.services.map((s) => (
                    <span key={s} className="analysis-chip">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {analysis.tone && (
              <div className="analysis-item">
                <span className="analysis-label">トーン</span>
                <p>{analysis.tone}</p>
              </div>
            )}
            {analysis.color_tendency && (
              <div className="analysis-item">
                <span className="analysis-label">配色傾向</span>
                <p>{analysis.color_tendency}</p>
              </div>
            )}
            {analysis.improvements && (
              <div className="analysis-item analysis-item-wide">
                <span className="analysis-label">改善ポイント（解消）</span>
                <p>{analysis.improvements}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}

      <div className="proposal-grid">
        {proposals.map((p, i) => (
          <button
            key={p.id}
            type="button"
            className={`proposal-card ${p.id === selectedId ? "on" : ""}`}
            onClick={() => setSelectedId(p.id)}
          >
            <div className="proposal-badge">{String(i + 1).padStart(2, "0")}</div>
            <span className="proposal-variant">{VARIANT_LABEL[p.variant] ?? p.variant}</span>
            {p.image_url ? (
              <img src={p.image_url} alt={VARIANT_LABEL[p.variant] ?? p.variant} />
            ) : (
              <div className="proposal-noimg muted">画像準備中</div>
            )}
            <span className="proposal-select-state">
              {p.id === selectedId ? "✓ 選択中" : "選択する"}
            </span>
          </button>
        ))}
      </div>

      <div className="card preview-card">
        <h2>選択中のデザインプレビュー</h2>
        <div className="preview-body">
          {selected.image_url && (
            <img className="preview-img" src={selected.image_url} alt="プレビュー" />
          )}
          <div className="preview-meta">
            <h3>{VARIANT_LABEL[selected.variant] ?? selected.variant}</h3>
            <p className="muted">{VARIANT_DESC[selected.variant]}</p>
            <p className="muted small">
              選択後、下層ページ（サービス・料金・事例・お問い合わせ等）も同じトーンで自動生成されます。
            </p>
          </div>
        </div>
      </div>

      <div className="wizard-actions">
        <button className="btn btn-ghost" onClick={() => navigate("/generating")}>
          ← 生成状況に戻る
        </button>
        <button className="btn btn-primary btn-lg" onClick={proceed} disabled={submitting}>
          {submitting ? "設定中..." : "このデザインで進める →"}
        </button>
      </div>
    </section>
  );
}
